import { createContext, useContext, useEffect } from "react";
import { PaneSlot } from "../../rail/Rail.styles";
import { paneStore } from "../paneStore";
import { FrameId } from "../paneRules";
import { PaneDefinition, toOpenPane } from "../definePane";

interface PanePosition {
    frame: FrameId;
    /** Left out outside a pane, which is how a page tells itself apart from a pane. */
    instanceId?: string;
    /** Where the pane is showing right now; a stage pane moved into the rail reports "right". */
    slot: PaneSlot;
    /** Whether the pane belongs on the stage, which is what earns it the fold controls. */
    isStagePane: boolean;
}

const PanePositionContext = createContext<PanePosition>({ frame: "first", slot: "right", isStagePane: false });

export const PanePositionProvider = PanePositionContext.Provider;

/** The pane the calling component is inside, or undefined on a page. */
export function useOwningPaneInstanceId(): string | undefined {
    return useContext(PanePositionContext).instanceId;
}

/**
 * What a pane knows about its own place. Pane content calls these rather than the slot-taking actions, so
 * an obligation form closes and dirties its own frame without knowing which one it is in.
 */
export function usePane() {
    const { frame, slot, isStagePane, instanceId } = useContext(PanePositionContext);

    return {
        slot,
        isStagePane,
        /** Opens a pane that belongs to this one, such as the document a form is about: it closes when this pane leaves. */
        openOwned: <TProps>(definition: PaneDefinition<TProps>, props: TProps) => paneStore.open(toOpenPane(definition, props, instanceId)),
        /** Closes without asking. For after a save, or once the user has confirmed. */
        close: () => paneStore.closeFrame(frame),
        /** What the close button and Cancel call: asks first when there is something to lose. */
        requestClose: () => paneStore.requestCloseFrame(frame),
        setHasUnsavedChanges: (value: boolean) => paneStore.setHasUnsavedChanges(frame, value),
    };
}

/** Throws away what the pane kept outside its own state, such as a session draft, once the user confirms a discard. */
export function useRegisterPaneDiscard(onDiscard: () => void) {
    const { frame } = useContext(PanePositionContext);

    // Re-registers on every render so the closure stays current without callers having to stabilise theirs.
    // Cheap, because registering only touches a Set.
    useEffect(() => paneStore.registerDiscard(frame, onDiscard));
}
